/**
 * Demo environment — points the Angular/Ionic front-end at the demo Cloudflare Worker
 * API (`rml-platform/apps/api`) instead of the legacy PHP/Strapi/Shopify stack.
 *
 * Shape mirrors `environment.ts` (see `.team/rml-web-api-mapping.md` §4 "Config / env")
 * so every not-yet-adapted legacy service (Strapi brochure, Shopify, PayFast redirect,
 * device management, …) still compiles against this file via Angular's `fileReplacements`
 * — those services are rewired in later "feature flow" tasks, not this one.
 *
 * SECURITY (mapping doc's "Security note" flag): this file carries NO live secrets.
 * `environment.ts`'s Strapi bearer JWT, `gm_api_key`, Shopify access token, and Firebase
 * web config are legacy values this demo does not need — every placeholder below is an
 * EMPTY string, kept only so a not-yet-rewired service's `environment.<key>` /
 * `settings["<key>"]` access still compiles/runs (it will simply fail closed — 404/empty
 * — until its own feature-flow task repoints it). Never fill these in with a real
 * credential without re-reading the mapping doc's security note first.
 */
export const environment = {
    production: false,

    /**
     * New API base (decisions/0009) — a single `/v1`-prefixed origin. New/rewired
     * services should build request paths as `${environment.apiBase}/auth/login` etc.
     * rather than the legacy pattern of splicing `settings["api_url"]` with a bare
     * RPC-style path.
     */
    apiBase: 'https://rml-api.systems-146.workers.dev/v1',

    // Public Supabase project config — publishable/anon key only (safe for a browser
    // bundle by design; never the service-role key, which never leaves the Worker).
    // Not consumed directly by any client service yet (the Worker is the only Supabase
    // caller in this pass) — kept here for a future client-side Supabase SDK use.
    supabaseUrl: '', // TODO(demo): fill in the demo Supabase project URL if a client-side SDK call is ever needed directly.
    supabaseAnonKey: 'sb_publishable_w8rtn1y8XPfMhZ2i1Movuw_LQCRH5dW',

    // TODO(demo): brochure/marketing content (Strapi) is not yet rewired to
    // `GET /v1/marketing/pages` (mapping doc §5 phase 4a) — empty placeholders (never
    // the real legacy JWT) purely so `strapi.service.ts` and the landing-v2 components
    // still compile; every call against these will fail closed until that phase lands.
    strapiUrl: '',
    strapiJWTToken: '',

    tenantDomain: 'regardingmylife.com',
    tenantDomainConfig: {
        'regardingmylife.com': {
            name: 'Regarding My Life (Demo)',
            // TODO(demo): legacy services still splice `api_url` + an RPC-style path
            // (e.g. `visitor/brochure/...`, `individual/internal/...`) — those paths
            // don't exist on the new Worker and are cut/deferred per the mapping doc's
            // gaps list; they 404 harmlessly until each is rewired in its own
            // feature-flow task. Auth (this task) uses `apiBase` above instead.
            api_url: 'https://rml-api.systems-146.workers.dev/',
            asset_url: '',
            userType: 'Member',
            pageTitle: 'Regarding My Life (Demo)',
            routing: {
                post_sign_in: 'member',
            },
            // TODO(demo): Shopify commerce has no equivalent in the new API (mapping doc
            // "Marketing / brochure" gaps) — placeholders only, no live credential.
            shopifyDomain: '',
            shopifyAccessToken: '',
            // PayFast sandbox host — both `POST /v1/subscriptions/checkout` and
            // `POST /v1/gifts/checkout` return their own signed `payfast.process_url`
            // (`CheckoutComponent`/`GiveAGiftComponent`, mapping doc §3 "Plans /
            // subscriptions"), which is always used first; this is a defensive fallback
            // only (`${payfastDomain}/eng/process`), never the primary source.
            payfastDomain: 'https://sandbox.payfast.co.za',
            // TODO(demo): no member account-deletion or social-login-redirect route in
            // the new API (mapping doc gaps) — placeholder only.
            domainURL: '',
        },
    },
};
